/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.SYSTEM.ASSETS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/AssetUploadPage.tsx. A DISTINCT
 * presentation (KPI summary + dense card list + bottom-sheet detail) over the
 * SAME data layer: it reads the branding Storage bucket via apiDb.storage
 * (list + public URL), exactly as the desktop page does. §16: never the desktop
 * grid re-hosted in a phone viewport.
 *
 * READ-MOSTLY on mobile by design: uploading, deleting and the "use as system
 * parameter" writes stay on the desktop screen (they are admin-desk workflows),
 * so this file performs NO business writes and does not import routed_writes.
 * The only client-side action is copying a public URL to the clipboard.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Image as ImageIcon, FileText, Copy, ExternalLink, X, Check } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

/** Storage bucket the desktop AssetUploadPage writes branding assets into. */
const BUCKET = 'branding';
const LIST_LIMIT = 200;
const BYTES_PER_KB = 1024;
const COPIED_RESET_MS = 2000;

interface AssetRow {
  name: string;
  size: number;
  url: string;
  updated_at: string;
  ext: string;
}

/** File extension (lowercase, no dot) — the asset list's one logical facet. */
function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}

/** Image extensions get the blue "image" lead; anything else is a neutral file. */
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif', 'bmp', 'ico']);
function isImage(ext: string): boolean {
  return IMAGE_EXTS.has(ext);
}

function formatSize(bytes: number): string {
  if (bytes >= BYTES_PER_KB * BYTES_PER_KB) return `${(bytes / (BYTES_PER_KB * BYTES_PER_KB)).toFixed(1)} MB`;
  return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`;
}

// Shape of a Storage object row returned by the gateway's storage_list.
interface StorageObject {
  name: string;
  updated_at?: string | null;
  created_at?: string | null;
  metadata?: { size?: number } | null;
}

export function MobileAssetUploadPage({ session: _session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [filter, setFilter] = useState('');
  const [typeFacet, setTypeFacet] = useState<string>('all');
  const [selected, setSelected] = useState<AssetRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.storage
        .from(BUCKET)
        .list('', { sortBy: { column: 'created_at', order: 'desc' }, limit: LIST_LIMIT });
      if (e) throw e;

      const rows: AssetRow[] = ((data ?? []) as StorageObject[])
        .filter((f) => f.name && !f.name.endsWith('/'))
        .map((f) => {
          const { data: pub } = apiDb.storage.from(BUCKET).getPublicUrl(f.name);
          return {
            name: f.name,
            size: f.metadata?.size ?? 0,
            url: pub.publicUrl,
            updated_at: f.updated_at ?? f.created_at ?? '',
            ext: fileExt(f.name),
          };
        });
      setAssets(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  // Distinct file-type facets derived from the loaded rows (§17.B5).
  const typeFacets = useMemo(() => {
    const exts = [...new Set(assets.map((a) => a.ext).filter(Boolean))].sort();
    return [{ key: 'all', label: t('mobile.assets.all', 'All') }, ...exts.map((ext) => ({ key: ext, label: ext.toUpperCase() }))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return assets.filter((a) => {
      if (typeFacet !== 'all' && a.ext !== typeFacet) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q);
    });
  }, [assets, filter, typeFacet]);

  const totalSize = useMemo(() => assets.reduce((sum, a) => sum + a.size, 0), [assets]);

  const openAsset = (url: string) => { window.open(url, '_blank', 'noopener,noreferrer'); };

  const copyUrl = async (url: string) => {
    setError(null);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      setError(t('mobile.assets.copy_failed', "Couldn't copy the URL to the clipboard."));
    }
  };

  const closeSheet = () => { setSelected(null); setCopied(false); };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.assets.title', 'Brand Assets')}
        description={t('mobile.assets.description', 'Files in the branding storage bucket, with their public URLs.')}
      />

      <MobileStatStrip
        stats={[
          { value: assets.length, label: t('mobile.assets.kpi_total', 'Assets') },
          { value: formatSize(totalSize), label: t('mobile.assets.kpi_size', 'Total size') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.assets.search', 'Search assets')}
          aria-label={t('mobile.assets.search', 'Search assets')}
        />
      </div>

      <MobileFilterChips
        chips={typeFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => typeFacet === v}
        onSelect={(v) => setTypeFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.assets.none', 'No assets match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((a) => (
            <MobileCardRow
              key={a.name}
              lead={isImage(a.ext) ? <ImageIcon /> : <FileText />}
              leadTone={isImage(a.ext) ? 'blue' : 'slate'}
              title={a.name}
              subtitle={`${formatSize(a.size)} · ${formatDateTime(a.updated_at)}`}
              trailing={a.ext ? <MobileStatusPill tone="info">{a.ext.toUpperCase()}</MobileStatusPill> : undefined}
              chevron
              onClick={() => { setSelected(a); setCopied(false); }}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={closeSheet} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.assets.detail', 'Asset detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{isImage(selected.ext) ? <ImageIcon size={20} /> : <FileText size={20} />}</span>
              <span>
                <div className="m-nm">{selected.name}</div>
                <div className="m-em">{selected.ext ? selected.ext.toUpperCase() : t('mobile.assets.type_unknown', 'File')} · {formatSize(selected.size)}</div>
              </span>
            </div>

            {isImage(selected.ext) && (
              <div style={{ padding: '10px 14px' }}>
                <img
                  src={selected.url}
                  alt={selected.name}
                  style={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 180,
                    objectFit: 'contain',
                    borderRadius: 10,
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-surface)',
                    // Transparent-image checkerboard so alpha assets read clearly.
                    backgroundImage:
                      'linear-gradient(45deg, var(--color-border) 25%, transparent 25%),' +
                      'linear-gradient(-45deg, var(--color-border) 25%, transparent 25%),' +
                      'linear-gradient(45deg, transparent 75%, var(--color-border) 75%),' +
                      'linear-gradient(-45deg, transparent 75%, var(--color-border) 75%)',
                    backgroundSize: '10px 10px',
                    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                  }}
                />
              </div>
            )}

            <MobileContentCard>
              <MobileDetailRow label={t('mobile.assets.file', 'File')} value={selected.name} />
              <MobileDetailRow
                label={t('mobile.assets.type', 'Type')}
                value={selected.ext ? selected.ext.toUpperCase() : t('mobile.assets.type_unknown', 'File')}
              />
              <MobileDetailRow label={t('mobile.assets.size', 'Size')} value={formatSize(selected.size)} />
              <MobileDetailRow
                label={t('mobile.assets.updated', 'Updated')}
                value={formatDateTime(selected.updated_at) || t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.assets.url', 'Public URL')}
                value={<span style={{ wordBreak: 'break-all', textAlign: 'left' }}>{selected.url}</span>}
              />
            </MobileContentCard>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" onClick={() => void copyUrl(selected.url)}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? t('mobile.assets.copied', 'Copied') : t('mobile.assets.copy_url', 'Copy URL')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" onClick={() => openAsset(selected.url)}>
                <ExternalLink size={16} />
                {t('mobile.assets.open', 'Open')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" onClick={closeSheet} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
